import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ThemeToggle, LanguageSelector, CurrencySelector, UnitSelector } from '../common';
import { Button } from '../common';
import { getLanguageFromPath, addLanguageToPath } from '../../lib/utils/geolocation';

interface HeaderProps {
  onSave?: () => void;
  onNewCycle?: () => void;
  isSaveDisabled?: boolean;
}

export function Header({
  onSave,
  onNewCycle,
  isSaveDisabled = false,
}: HeaderProps) {
  const { t } = useTranslation();
  const location = useLocation();
  
  const currentLang = getLanguageFromPath(location.pathname) || 'en';
  
  const getLocalizedPath = (path: string) => {
    return addLanguageToPath(path, currentLang);
  };

  // Aktif menü öğesini belirlemek için fonksiyon
  const isActivePage = (path: string) => {
    const currentPath = location.pathname;
    const targetPath = getLocalizedPath(path);
    
    // Ana sayfa kontrolü
    if (path === '/') {
      return currentPath === targetPath || 
             (currentLang === 'en' && currentPath === '/') ||
             (currentLang === 'tr' && currentPath === '/tr');
    }
    
    // Blog sayfası için özel kontrol (blog post sayfaları da dahil)
    if (path === '/blog') {
      return currentPath.startsWith(targetPath);
    }
    
    // Diğer sayfalar için tam eşleşme
    return currentPath === targetPath;
  };

  // Aktif menü öğesi için CSS sınıfları
  const getNavLinkClasses = (path: string) => {
    const baseClasses = "text-xs font-medium transition-colors px-2 py-1 rounded relative";
    const isActive = isActivePage(path);
    
    if (isActive) {
      return `${baseClasses} text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-b-2 border-blue-500`;
    }
    
    return `${baseClasses} text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700`;
  };

  return (
    <header className="bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 py-2 shadow-sm">
      <div className="flex flex-col gap-2">
        {/* First Row: Logo centered, Navigation + Action Buttons on right (Desktop) */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 justify-center sm:justify-start">
            <a
              href={getLocalizedPath('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              onClick={(e) => {
                // Eğer zaten hesaplama sayfasındaysa, sayfayı yenile
                const currentPath = location.pathname;
                const homePath = getLocalizedPath('/');
                const isHomePage = currentPath === homePath || 
                                 (currentLang === 'en' && currentPath === '/') ||
                                 (currentLang === 'tr' && currentPath === '/tr');
                
                if (isHomePage) {
                  e.preventDefault();
                  window.location.reload();
                }
              }}
            >
              <span className="text-2xl">🐔</span>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {t('header.title') || 'Broiler Muhasebe Sistemi'}
              </h1>
            </a>
          </div>
          
          {/* Navigation Links + Action Buttons - Right side on desktop, hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2">
            {/* Action Buttons - Show on all pages */}
            {(onNewCycle || onSave) && (
              <div className="flex items-center gap-1">
                {onNewCycle && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onNewCycle}
                    aria-label="Reset calculation data"
                    className="text-xs px-2 py-1"
                  >
                    🔄 {t('common.newCycle')}
                  </Button>
                )}

                {onSave && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onSave}
                    disabled={isSaveDisabled}
                    aria-label="Save"
                    className="text-xs px-2 py-1"
                  >
                    💾 {t('common.save')}
                  </Button>
                )}
              </div>
            )}
            
            <nav className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-2">
              <a
                href={getLocalizedPath('/')}
                className={getNavLinkClasses('/')}
              >
                📊 {t('header.calculation')}
              </a>
              <a
                href={getLocalizedPath('/comparison')}
                className={getNavLinkClasses('/comparison')}
              >
                📈 {t('header.comparison')}
              </a>
              <a
                href={getLocalizedPath('/blog')}
                className={getNavLinkClasses('/blog')}
              >
                📝 {t('header.blog')}
              </a>
              <a
                href={getLocalizedPath('/settings')}
                className={getNavLinkClasses('/settings')}
              >
                ⚙️ {t('header.settings')}
              </a>
            </nav>
          </div>
        </div>
        
        {/* Second Row: Navigation Links (Mobile) */}
        <nav className="flex sm:hidden items-center gap-2 w-full justify-center">
          <a
            href={getLocalizedPath('/')}
            className={getNavLinkClasses('/')}
          >
            📊 {t('header.calculation')}
          </a>
          <a
            href={getLocalizedPath('/comparison')}
            className={getNavLinkClasses('/comparison')}
          >
            📈 {t('header.comparison')}
          </a>
          <a
            href={getLocalizedPath('/blog')}
            className={getNavLinkClasses('/blog')}
          >
            📝 {t('header.blog')}
          </a>
          <a
            href={getLocalizedPath('/settings')}
            className={getNavLinkClasses('/settings')}
          >
            ⚙️ {t('header.settings')}
          </a>
        </nav>

        {/* Third Row: Selectors + Save Button (Desktop: right, Mobile: center) */}
        <div className="flex items-center justify-center sm:justify-end gap-1.5">
          <div className="flex items-center gap-1">
            <UnitSelector />
            <CurrencySelector />
            <LanguageSelector />
            <ThemeToggle />
          </div>
          
          {/* Save Button - Show on mobile in this row */}
          {onSave && (
            <div className="flex sm:hidden items-center border-l border-gray-300 dark:border-gray-600 pl-1.5">
              <Button
                variant="secondary"
                size="sm"
                onClick={onSave}
                disabled={isSaveDisabled}
                aria-label="Save"
                className="text-xs px-2 py-1"
              >
                💾 {t('common.save')}
              </Button>
            </div>
          )}
        </div>

        {/* Fourth Row: Reset Button - Mobile only, centered */}
        {onNewCycle && (
          <div className="flex sm:hidden items-center w-full justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={onNewCycle}
              aria-label="Reset calculation data"
              className="text-xs px-2 py-1"
            >
              🔄 {t('common.newCycle')}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
